/* PDP Inline Video Hero
   Scoped web component — each <pdp-inline-video-hero> instance manages only
   its own children, so multiple sections on a page work independently.
   On play: adds the --playing class and starts the video inline
   (no popup / modal / redirect). */

(function () {
  'use strict';

  if (customElements.get('pdp-inline-video-hero')) return;

  class PdpInlineVideoHero extends HTMLElement {
    connectedCallback() {
      this.playButton = this.querySelector('[data-pdp-ivh-play]');
      this.videoWrap = this.querySelector('.pdp-inline-video-hero__video');
      if (!this.playButton || !this.videoWrap) return;

      this.playButton.addEventListener('click', this.play.bind(this));
    }

    play() {
      if (this.classList.contains('pdp-inline-video-hero--playing')) return;

      // 1) A server-rendered <video>, if one is ever present.
      var nativeVideo = this.videoWrap.querySelector('video');
      if (nativeVideo) {
        this.classList.add('pdp-inline-video-hero--playing');
        var playPromise = nativeVideo.play();
        if (playPromise && typeof playPromise.catch === 'function') {
          // If playback is blocked, fall back to the visible native controls.
          playPromise.catch(function () {});
        }
        return;
      }

      var url = this.dataset.externalVideoUrl;
      if (!url) return;

      // 2) YouTube / Vimeo -> embed iframe.
      var embedUrl = this.buildEmbedUrl(url);
      if (embedUrl) {
        var iframe = document.createElement('iframe');
        iframe.src = embedUrl;
        iframe.setAttribute('allow', 'autoplay; encrypted-media; picture-in-picture');
        iframe.setAttribute('allowfullscreen', '');
        iframe.setAttribute('title', 'Video player');
        iframe.setAttribute('loading', 'eager');
        this.videoWrap.appendChild(iframe);
        this.classList.add('pdp-inline-video-hero--playing');
        return;
      }

      // 3) Direct video file (.mp4/.mov/.webm…) or Shopify CDN/Files URL
      //    -> inject an HTML5 <video> and play it inline. The existing
      //    .pdp-inline-video-hero__video video CSS sizes it to fill.
      if (this.isDirectVideoUrl(url)) {
        var video = document.createElement('video');
        video.src = url;
        video.setAttribute('controls', '');
        video.setAttribute('playsinline', '');
        video.setAttribute('preload', 'metadata');
        this.videoWrap.appendChild(video);
        this.classList.add('pdp-inline-video-hero--playing');
        var filePromise = video.play();
        if (filePromise && typeof filePromise.catch === 'function') {
          filePromise.catch(function () {});
        }
        return;
      }

      // Unrecognised URL: leave poster/overlay in place (no broken embed).
    }

    /* True for direct video files (by extension) or Shopify-hosted video URLs. */
    isDirectVideoUrl(url) {
      if (!url) return false;
      var path = url.split('#')[0].split('?')[0].toLowerCase();
      if (/\.(mp4|m4v|mov|webm|ogv|ogg)$/.test(path)) return true;
      // Shopify CDN video delivery (e.g. cdn.shopify.com/videos/...) and Files.
      if (/cdn\.shopify\.com\/videos\//.test(url)) return true;
      if (/cdn\.shopify\.com\/.+\/files\/.+\.(mp4|m4v|mov|webm)/i.test(url)) return true;
      return false;
    }

    /* Converts a YouTube / Vimeo page URL into an autoplaying embed URL.
       Returns null for anything unrecognised. */
    buildEmbedUrl(url) {
      if (!url) return null;

      var youtube = url.match(
        /(?:youtube\.com\/(?:watch\?(?:.*&)?v=|shorts\/|embed\/)|youtu\.be\/)([A-Za-z0-9_-]{6,})/
      );
      if (youtube) {
        return 'https://www.youtube.com/embed/' + youtube[1] + '?autoplay=1&rel=0&playsinline=1';
      }

      var vimeo = url.match(/vimeo\.com\/(?:video\/)?(\d+)/);
      if (vimeo) {
        return 'https://player.vimeo.com/video/' + vimeo[1] + '?autoplay=1';
      }

      return null;
    }
  }

  customElements.define('pdp-inline-video-hero', PdpInlineVideoHero);
})();
